import { useState } from "react"
import type { ChangeEvent, ComponentProps } from "react"
import { cn } from "cn"

/**
 * Figma-Component-Set „Text Area" (node 2184:6047) bündelt Label, Feld und
 * Helfertext/Inline-Alert in einem Frame — analog zu `Input`/`Select` hier
 * bewusst NICHT als Props nachgebaut. Für Label/Description/Error das
 * generische `Field`-Kit verwenden (siehe „V2/Guides/Formulare").
 *
 * Die States (default/focus/filled/disabled/invalid) sind keine Props,
 * sondern ergeben sich aus `:focus`, Inhalt, `disabled` und `aria-invalid`.
 */
export type TextAreaProps = ComponentProps<"textarea"> & {
  /** Zeigt den Zeichenzähler („0/250") unten rechts; nutzt `maxLength`. */
  showCount?: boolean
}

// Figma „Text area resize handle" (2201:5562): zwei diagonale Linien, 15 px.
const RESIZE_HANDLE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='15' height='15' fill='none' stroke='%23000e46' stroke-linecap='round'%3E%3Cpath d='M13 5 5 13M13 10l-3 3'/%3E%3C/svg%3E\")"

function TextArea({
  className,
  showCount = false,
  disabled,
  maxLength,
  value,
  defaultValue,
  onChange,
  ...props
}: TextAreaProps) {
  const [uncontrolledLength, setUncontrolledLength] = useState(
    String(defaultValue ?? "").length
  )
  const length = value !== undefined ? String(value).length : uncontrolledLength

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setUncontrolledLength(event.target.value.length)
    onChange?.(event)
  }

  return (
    <div
      data-slot="text-area-wrapper"
      className={cn(
        "relative flex w-full rounded-xl border-2 border-neutrals-v2-200 bg-backgrounds-primary text-sm leading-5 transition-colors has-[[data-slot=text-area]:disabled]:cursor-not-allowed has-[[data-slot=text-area]:focus]:border-labels-primary has-[[data-slot=text-area][aria-invalid=true]]:border-error-red-200",
        className
      )}
    >
      <textarea
        data-slot="text-area"
        disabled={disabled}
        maxLength={maxLength}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        style={
          {
            "--text-area-resize-handle": RESIZE_HANDLE,
          } as React.CSSProperties
        }
        className={cn(
          "min-h-17 min-w-0 flex-1 resize-y bg-transparent pt-3 pr-4 pb-8 pl-4 text-labels-primary outline-none placeholder:text-labels-secondary disabled:cursor-not-allowed disabled:text-labels-tertiary",
          "[&::-webkit-resizer]:bg-(image:--text-area-resize-handle) [&::-webkit-resizer]:bg-no-repeat"
        )}
        {...props}
      />
      {showCount && (
        <span
          data-slot="text-area-count"
          className="pointer-events-none absolute right-5 bottom-0 py-2 text-xs leading-4 text-labels-primary"
        >
          {length}
          {maxLength !== undefined && `/${maxLength}`}
        </span>
      )}
    </div>
  )
}
TextArea.displayName = "TextArea"

export { TextArea }
